import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Save } from 'lucide-react';
import { CO_SCHOLASTIC_TERMS, CoScholasticApi, CoScholasticTerm, FEEDBACK_TIER_OPTIONS, FeedbackTier } from '@/services/co-scholastic.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { StudentsApi } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { usePermissions } from '@/hooks/usePermissions';

const TIER_LABEL = Object.fromEntries(FEEDBACK_TIER_OPTIONS.map((o) => [o.value, o.label]));
const TIER_TONE: Record<string, 'green' | 'amber' | 'red' | 'slate'> = {
  excellent: 'green',
  good: 'green',
  basic: 'amber',
  need_improvement: 'red',
};

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.response?.data?.message ?? anyE?.message ?? 'Something went wrong';
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Co-scholastic Assessment entry — one student at a time, matching
 * Exams' "by student" marks-entry pattern (MarksByStudentEntry) rather
 * than the full-class grid variant: the fixed API only exposes a
 * per-student read/write, no class-wide bulk endpoint, so a per-student
 * row-of-categories form is the pattern that actually fits.
 *
 * Two save modes, both real legacy patterns per the survey: "Save one at
 * a time" loops individual POST .../assessments calls (the web survey's
 * behavior — each category is its own independent write); "Batch save"
 * sends one POST .../assessments/batch call covering every graded
 * category (matching the frozen mobile contract's submit-co-scholastics
 * payload shape exactly: student_id, term, assessment_date,
 * categories:[{category_id, grade, remarks}]). Batch is also
 * transactionally atomic (one runInSchema call) where individual isn't.
 */
export function CoScholasticPage() {
  const { can } = usePermissions();
  const canWrite = can('/co-scholastic', 'create');
  const qc = useQueryClient();

  const { data: categories = [] } = useQuery({
    queryKey: ['co-scholastic-categories'],
    queryFn: () => CoScholasticApi.categories(),
  });

  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-co-scholastic'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-co-scholastic'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];

  const [academicYearId, setAcademicYearId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [term, setTerm] = useState<CoScholasticTerm>('Term 1');
  const [assessmentDate, setAssessmentDate] = useState(todayIso());
  const [saveMode, setSaveMode] = useState<'individual' | 'batch'>('individual');

  useEffect(() => {
    if (years.length && !academicYearId) setAcademicYearId(years.find((y: any) => y.isCurrent)?.id ?? years[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: studentsPage } = useQuery({
    queryKey: ['students', 'co-scholastic', classId, sectionId, academicYearId],
    queryFn: () => StudentsApi.list({ classId, sectionId: sectionId || undefined, academicYearId, limit: 100 }),
    enabled: !!classId && !!academicYearId,
  });
  const students = studentsPage?.items ?? [];

  const { data: assessments = [] } = useQuery({
    queryKey: ['co-scholastic-assessments', studentId],
    queryFn: () => CoScholasticApi.assessments(studentId),
    enabled: !!studentId,
  });

  const [tiers, setTiers] = useState<Record<string, FeedbackTier | ''>>({});
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  useEffect(() => {
    const initTiers: Record<string, FeedbackTier | ''> = {};
    const initRemarks: Record<string, string> = {};
    let latestDate: string | undefined;
    for (const a of assessments) {
      if (a.academicYearId === academicYearId && a.term === term) {
        initTiers[a.categoryId] = a.feedbackTier;
        initRemarks[a.categoryId] = a.remarks ?? '';
        if (!latestDate || a.assessmentDate > latestDate) latestDate = a.assessmentDate;
      }
    }
    setTiers(initTiers);
    setRemarks(initRemarks);
    setAssessmentDate(latestDate ?? todayIso());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, assessments, academicYearId, term]);

  const [saveError, setSaveError] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: async () => {
      setSaveError(undefined);
      const graded = categories.filter((cat) => tiers[cat.id]);
      if (!graded.length) throw new Error('Set a feedback tier for at least one category');

      if (saveMode === 'batch') {
        const result = await CoScholasticApi.batchSetAssessment({
          studentId,
          academicYearId,
          term,
          assessmentDate,
          categories: graded.map((cat) => ({
            categoryId: cat.id,
            feedbackTier: tiers[cat.id] as FeedbackTier,
            remarks: remarks[cat.id]?.trim() || undefined,
          })),
        });
        return { saved: result.length };
      }

      let saved = 0;
      for (const cat of graded) {
        await CoScholasticApi.setAssessment({
          studentId,
          categoryId: cat.id,
          academicYearId,
          term,
          feedbackTier: tiers[cat.id] as FeedbackTier,
          assessmentDate,
          remarks: remarks[cat.id]?.trim() || undefined,
        });
        saved++;
      }
      return { saved };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['co-scholastic-assessments', studentId] }),
    onError: (e) => setSaveError(errMsg(e)),
  });

  const history = [...assessments]
    .filter((a) => a.academicYearId === academicYearId)
    .sort((a, b) => a.categoryName.localeCompare(b.categoryName) || a.term.localeCompare(b.term));

  return (
    <>
      <PageHeader title="Co-scholastic Assessment" description="Social Skills, Physical Development, Creativity & Arts, Work Habits, Environmental Responsibility — the real school rubric." />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Academic Year">
          <Select value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
            {years.map((y: any) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Class">
          <Select
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setSectionId('');
              setStudentId('');
            }}
          >
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Section">
          <Select
            value={sectionId}
            onChange={(e) => {
              setSectionId(e.target.value);
              setStudentId('');
            }}
            disabled={!classId}
          >
            <option value="">All sections</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Student">
          <Select value={studentId} onChange={(e) => setStudentId(e.target.value)} disabled={!classId}>
            <option value="">Select student…</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name ?? '(no linked user)'} {s.rollNumber ? `— Roll ${s.rollNumber}` : ''} ({s.admissionNo})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Term">
          <Select value={term} onChange={(e) => setTerm(e.target.value as CoScholasticTerm)}>
            {CO_SCHOLASTIC_TERMS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Assessment Date">
          <Input type="date" value={assessmentDate} onChange={(e) => setAssessmentDate(e.target.value)} className="!w-40" />
        </Field>
      </div>

      {!studentId ? (
        <div className="card p-8 text-center text-slate-400">Select a student to assess.</div>
      ) : (
        <div className="card p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex gap-1 rounded-md bg-slate-100 p-1 text-sm">
              <button
                type="button"
                className={`rounded px-3 py-1 ${saveMode === 'individual' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
                onClick={() => setSaveMode('individual')}
              >
                Save one at a time
              </button>
              <button
                type="button"
                className={`rounded px-3 py-1 ${saveMode === 'batch' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
                onClick={() => setSaveMode('batch')}
              >
                Batch save
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {categories.map((cat) => (
              <div key={cat.id} className="rounded-md border border-slate-200 p-3">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{cat.name}</div>
                    {cat.objectives.length > 0 && (
                      <ul className="mt-1 flex items-start gap-1 text-xs text-slate-500">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span>{cat.objectives.join(' · ')}</span>
                      </ul>
                    )}
                  </div>
                  <Select
                    className="!w-48 shrink-0"
                    value={tiers[cat.id] ?? ''}
                    onChange={(e) => setTiers((p) => ({ ...p, [cat.id]: e.target.value as FeedbackTier }))}
                    disabled={!canWrite}
                  >
                    <option value="">Not assessed</option>
                    {FEEDBACK_TIER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {tiers[cat.id] && (
                  <>
                    <p className="mb-2 text-xs italic text-slate-400">{cat.feedbackTiers[toFeedbackKey(tiers[cat.id] as FeedbackTier)]}</p>
                    <Textarea
                      placeholder="Remarks (optional)"
                      rows={2}
                      value={remarks[cat.id] ?? ''}
                      onChange={(e) => setRemarks((p) => ({ ...p, [cat.id]: e.target.value }))}
                      disabled={!canWrite}
                      className="text-sm"
                    />
                  </>
                )}
              </div>
            ))}
          </div>

          {canWrite && (
            <div className="mt-4 flex items-center justify-end gap-3">
              {save.isSuccess && <span className="text-sm text-green-700">✓ Saved {save.data?.saved} categories.</span>}
              {saveError && <span className="text-sm text-red-600">{saveError}</span>}
              <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
                <Save className="mr-1.5 h-4 w-4" />
                {save.isPending ? 'Saving…' : `Save ${term} assessment`}
              </button>
            </div>
          )}

          {history.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-4">
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Progress this year</h3>
              <div className="space-y-1">
                {history.map((a) => (
                  <div key={a.id} className="rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">
                        {a.categoryName} <span className="text-slate-400">· {a.term} · {a.assessmentDate}</span>
                      </span>
                      <Badge tone={TIER_TONE[a.feedbackTier] ?? 'slate'}>{TIER_LABEL[a.feedbackTier] ?? a.feedbackTier}</Badge>
                    </div>
                    {a.remarks && <p className="mt-0.5 pl-0 text-xs italic text-slate-400">{a.remarks}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function toFeedbackKey(tier: FeedbackTier): 'needImprovement' | 'basic' | 'good' | 'excellent' {
  return tier === 'need_improvement' ? 'needImprovement' : tier;
}
